
import { useState, useEffect } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  gamesSessionsQuery,
  courseMembersQuery,
  thePrinciplesDecisionsQuery,
  getPrinciplesScoresPrinciples,
  getThePrinciplesDecisionsInsights
} from '../api';

import './styles.css';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';
import InstructorTransactionsModal from '../../../UI/instructor/decisionInsights/transactionsModal';

import { 
  popupMessagesDecisionInsightsPrinciples,
  popupMessagesDecisionInsightsNoGamesData,
  decisionBreakdownElementsHeadersThePrinciples
} from './data';

export default function InstructorInsightsPrinciples() {
  const navigate = useNavigate();

  const [isModalShown, setIsModalShown] = useState(false);
  const [isDetailsButtonLoading, setIsDetailsButtonLoading] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const [filterBy, setFilterBy] = useState('Accuracy');
  const [data, setData] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);

  const [popupMessages, setPopupMessages] = useState([]);
  const [detailsButtonText, setDetailsButtonText] = useState(null);

  const [thePrinciplesGameIds, setThePrinciplesGameIds] = useState([]);

  const filterByOptions = ['Accuracy', 'Points'];

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: gamesSessions } = useQuery(gamesSessionsQuery()) || {};

  const { data: decisions } = useQuery({
    ...thePrinciplesDecisionsQuery(),
    enabled: shouldFetchDetails,
  });

  const { data: courseMembers } = useQuery(courseMembersQuery()) || {};

  const { data: principlesScoresPrinciples } = useQuery({
    queryKey: ['principlesScoresPrinciples'],
    queryFn: async () => getPrinciplesScoresPrinciples({completed: true}),
    refetchOnWindowFocus: false
  });

  const thePrinciplesDecisionsQueries = useQueries({
    queries: thePrinciplesGameIds.map(gameId => ({
      queryKey: ['thePrinciplesDecisionsInsights', gameId],
      queryFn: () => getThePrinciplesDecisionsInsights({ gameId }),
      refetchOnWindowFocus: false,
    }))
  });

  useEffect(() => {
    if(principlesScoresPrinciples && courseMembers?.length > 0) {
      formatLeaderboardData();
    }
  }, [principlesScoresPrinciples, filterBy, courseMembers]);

  const calculateAverage = (data, param) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record[param];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };

  const formatLeaderboardData = () => {
    const instructorIds = new Set(
      courseMembers
        .filter(member => member.is_instructor)
        .map(member => member.user_id)
    );

    const principlesScoresFiltered = principlesScoresPrinciples.filter(score => !instructorIds.has(score.user)); 

    const revenueRecognitionPrincipleScores = principlesScoresFiltered.filter(principlesScoresPrinciple => principlesScoresPrinciple.principle_name === 'Revenue Recognition');
    const revenueRecognitionScore = filterBy === 'Accuracy' ? calculateAverage(revenueRecognitionPrincipleScores, 'total_accuracy') : calculateAverage(revenueRecognitionPrincipleScores, 'total_points');
     
    const expenseRecognitionPrincipleScores = principlesScoresFiltered.filter(principlesScoresPrinciple => principlesScoresPrinciple.principle_name === 'Expense Recognition');
    const expenseRecognitionScore = filterBy === 'Accuracy' ? calculateAverage(expenseRecognitionPrincipleScores, 'total_accuracy') : calculateAverage(expenseRecognitionPrincipleScores, 'total_points');

    const financialAndOtherPrincipleScores = principlesScoresFiltered.filter(principlesScoresPrinciple => principlesScoresPrinciple.principle_name !== 'Revenue Recognition' && principlesScoresPrinciple.principle_name !== 'Expense Recognition');
    const financialAndOtherScore = filterBy === 'Accuracy' ? calculateAverage(financialAndOtherPrincipleScores, 'total_accuracy') : calculateAverage(financialAndOtherPrincipleScores, 'total_points');
 
    const sectionData = [
      ...(revenueRecognitionScore ? [{ title: 'Revenue Recognition', score: revenueRecognitionScore * 100 || 0, color: '#C7DBFF' }] : []),
      ...(expenseRecognitionScore ? [{ title: 'Expense Recognition', score: expenseRecognitionScore * 100 || 0, color: '#C7DBFF' }] : []),
      ...(financialAndOtherScore ? [{ title: 'Financing Activities & Other', score: financialAndOtherScore * 100 || 0, color: '#C7DBFF' }] : [])
    ];

    const data = [
      ...(sectionData.length > 0 ? [{
        title: 'Accuracy By Decision Type',
        bars: sectionData,
        barsLabel: '%'
      }] : []),
    ];

    setData(data);
  }

  useEffect(() => {
    if (games && shouldFetchDetails) {
      const thePrinciplesGames = games.filter(game => game.game_type === 'The Principles');

      const thePrinciplesGameIds = thePrinciplesGames.map(game => game.id);
      
      setThePrinciplesGameIds(thePrinciplesGameIds);
      setShouldFetchDetails(false);
    }
  }, [games, shouldFetchDetails]);

  useEffect(() => {
      if (games?.length > 0 && gamesSessions && courseMembers?.length > 0) {
        const instructorIds = new Set(
          courseMembers
            .filter(member => member.is_instructor)
            .map(member => member.user_id)
        );

        const hasCompletedAccountGame = games.some(game => 
          (game.game_type === 'The Principles') &&
          gamesSessions.some(session => session.game === game.id && session.completed === true && !instructorIds.has(session.user))
        );
        console.log('hasCompletedAccountGame', hasCompletedAccountGame);
        if(hasCompletedAccountGame) {
          setPopupMessages(popupMessagesDecisionInsightsPrinciples);
          setDetailsButtonText('View Details');
        } else {
          setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
        }
      }
    }, [games, gamesSessions, courseMembers]);

  const allDecisionsLoaded = thePrinciplesDecisionsQueries.every(query => query.data) && thePrinciplesGameIds.length > 0;

  const getUniqueTransactionsByLowestQuestionId = (allThePrinciplesDecisions) => {
    return allThePrinciplesDecisions.reduce((acc, item) => {
      const existingItem = acc.find(existing => existing.transaction_id === item.transaction_id);
      
      if (!existingItem || item.question_id < existingItem.question_id) {
        // Remove existing item with same transaction_id if it exists
        const filteredAcc = acc.filter(existing => existing.transaction_id !== item.transaction_id);
        filteredAcc.push(item);
        return filteredAcc;
      }
      
      return acc;
    }, []);
  };

  useEffect(() => {
    if (allDecisionsLoaded && decisions?.length > 0 && courseMembers?.length > 0) {
      const membersCount = courseMembers.filter(courseMember => courseMember.is_instructor !== true).length;

      const allThePrinciplesDecisionsData = thePrinciplesDecisionsQueries.map(query => query.data);
      const allThePrinciplesDecisions = allThePrinciplesDecisionsData
        .filter(subArray => subArray.length > 0)
        .flat();

      const uniqueTransactionInsights = getUniqueTransactionsByLowestQuestionId(allThePrinciplesDecisions);
      const header = 'Impact on Profits';
      
      const transactionsData = [];
      uniqueTransactionInsights.forEach(decisionInsight => {
        const row = {
          question: decisionInsight.question,
        };

        const relatedDecision = decisions.find(decision => 
          decision.question === decisionInsight.question_id && decision.selected_option === decisionInsight.options[0].option
        );
        const isCorrect = relatedDecision?.is_correct;

        let value;
        let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
        if(!isCorrect && decisionInsight.options[0].count_users >= Math.min(20, 0.2 * membersCount)) {
          value = `<span class="white">${decisionInsight.options[0].option}<span class="decision-insights-count">${decisionInsight.options[0].count_users}</span></span>`;
          backgroundColor = '#FD9595';
        } else {
          value = decisionInsight.options[0].option;
        }
        
        if (row[header]) {
          row[header].push({
            value,
            isCorrect,
            background: backgroundColor,
          });
        } else {
          row[header] = [{
            value,
            background: backgroundColor,
          }];
        }
        
        transactionsData.push(row);
      });
      
      setTransactionsData(transactionsData);
      setIsModalShown(true);
      setIsDetailsButtonLoading(false);
    }
  }, [allDecisionsLoaded, decisions, courseMembers]);

  const handlePrevButtonClick = () => {
    let redirectUrl = '/instructor/insights/decision-insights/overall';
    if(games && games.length > 0) {
      if(games.some(game => game.game_type === 'Balanced Equation')) {
        redirectUrl = '/instructor/insights/decision-insights/double-entry';
      } else if(games.some(game => game.game_type === 'Account Classification' || game.game_type === 'Normal Balance')) {
        redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
      } 
    }
    navigate(redirectUrl);
  }

  const handleNextButtonClick = () => {
    navigate('/instructor/insights/progress-scores/progress' );
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.map(bar => bar.score) || []
  ));

  const handleDetailsButtonClick = () => {
    setIsDetailsButtonLoading(true);
    setShouldFetchDetails(true);
  }

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
    
    setThePrinciplesGameIds([]);
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={4}
      pagesQuantity={4}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>INSIGHTS | OVERALL <b>| PRINCIPLES</b></h1>
      <div className="decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores 
          data={data}
          size="small"
          maxScore={maxScore}
          isBigScores={true}
          alignBarTitle="center"
          detailsButtonText={detailsButtonText}
          onDetailsButtonClick={handleDetailsButtonClick}
          isDetailsButtonLoading={isDetailsButtonLoading}
        />
        {isModalShown && (
          <InstructorTransactionsModal
            onCloseButtonClick={handleModalCloseButtonClick}
            tableHeaders={decisionBreakdownElementsHeadersThePrinciples}
            transactionsData={transactionsData}
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
